import { useState, useEffect, useRef } from "react";
import { Stepper, StepContent } from "./Stepper.jsx";
import { Button } from "../ui/button.tsx";
import TopicsStep from "./TopicStep/index.jsx";
import CourseInformationStep from "./courseInformationStep/index.jsx";
import AssessmentBuilderStep from "./AssessmentBuilderStep/index.jsx";
import { getCourseBuilderData } from "@/api/courseApi.ts";
import { courseBuilderStateUtils } from "@/utils/course-builder/courseBuilderUtils";
import { buildStepDataFromBuilderResponse } from "./utils/mapBuilderResponse";

export const CourseBuilderForm = ({
  currentStep,
  setCurrentStep,
  mode = "ADD",
  setMode,
  courseId,
}) => {
  const stepDataRefs = useRef({
    courseInfo: null,
    topics: null,
    assessment: null,
  });
  const [stepData, setStepData] = useState({
    courseInfo: null,
    topics: null,
    assessment: null,
  });

  // Load saved state on component mount
  useEffect(() => {
    const savedState = courseBuilderStateUtils.getState();
    if (savedState.mode !== mode) {
      setMode(savedState.mode);
    }
    if (savedState.currentStep !== currentStep) {
      setCurrentStep(savedState.currentStep);
    }
  }, []);

  // Save state changes to localStorage
  useEffect(() => {
    courseBuilderStateUtils.saveState(mode, currentStep);
  }, [mode, currentStep]);

  // Load data from localStorage for previous step navigation
  const loadStepDataFromLocalStorage = (stepIndex) => {
    try {
      switch (stepIndex) {
        case 0: // Course Information
          const courseData = localStorage.getItem("courseData");
          if (courseData) {
            const parsed = JSON.parse(courseData);
            stepDataRefs.current.courseInfo = parsed;
            setStepData((prev) => ({ ...prev, courseInfo: parsed }));
          }
          break;
        case 1: // Topics
          const topicsData = localStorage.getItem("topics");
          if (topicsData) {
            const parsed = JSON.parse(topicsData);
            stepDataRefs.current.topics = parsed;
            setStepData((prev) => ({ ...prev, topics: parsed }));
          }
          break;
        case 2: // Assessment
          const assessmentData = localStorage.getItem("assessment");
          if (assessmentData) {
            const parsed = JSON.parse(assessmentData);
            stepDataRefs.current.assessment = parsed;
            setStepData((prev) => ({ ...prev, assessment: parsed }));
          }
          break;
        default:
          break;
      }
    } catch (error) {
      console.warn("Failed to load step data from localStorage:", error);
    }
  };

  // Enhanced step navigation with data loading
  const handleStepNavigation = (direction, targetStep) => {
    if (direction === "previous" && targetStep < currentStep) {
      // Load data for the target step from localStorage
      loadStepDataFromLocalStorage(targetStep);
    }
    setCurrentStep(targetStep);
  };

  const fetchCourseDetails = async () => {
    if (!courseId) return;

    // One request for the whole tree. This used to be getCourse + one
    // getTest per learning material + searchTests, which on a large course
    // meant ~80 parallel requests and mostly 5xx back.
    const response = await getCourseBuilderData(courseId, [
      "tests",
      "assessment",
    ]);
    if (!response.success) return;

    const {
      courseInfo: courseDataFormatted,
      topics: topicsDataFormatted,
      assessment: assessmentDataFormatted,
    } = buildStepDataFromBuilderResponse(response.data);

    stepDataRefs.current.courseInfo = courseDataFormatted;
    stepDataRefs.current.topics = topicsDataFormatted;
    stepDataRefs.current.assessment = assessmentDataFormatted;
    setStepData({
      courseInfo: courseDataFormatted,
      topics: topicsDataFormatted,
      assessment: assessmentDataFormatted,
    });
    localStorage.setItem("courseData", JSON.stringify(courseDataFormatted));
    localStorage.setItem("topics", JSON.stringify(topicsDataFormatted));
    localStorage.setItem("assessment", JSON.stringify(assessmentDataFormatted));
  };

  useEffect(() => {
    fetchCourseDetails();
  }, [courseId]);

  // Handlers to receive data from each step when needed
  const handleCourseInfoData = (data) => {
    stepDataRefs.current.courseInfo = data;
  };

  const handleTopicData = (data) => {
    stepDataRefs.current.topics = data;
  };

  const handleAssessmentData = (data) => {
    stepDataRefs.current.assessment = data;
  };

  // Function to get all collected data when needed
  // const getAllStepData = () => {
  //   return {
  //     courseInfo: stepDataRefs.current.courseInfo,
  //     topics: stepDataRefs.current.topics,
  //     assessment: stepDataRefs.current.assessment,
  //   };
  // };

  // Function to handle final form submission
  // const handleFinalSubmit = () => {
  //   const allData = getAllStepData();
  //   // Process final submission here
  // };

  const steps = [
    {
      id: "step-1",
      title: "Course Information",
      description: "Add basic information about your course",
      component: (
        <CourseInformationStep
          mode={mode}
          onDataChange={handleCourseInfoData}
          setCurrentStep={setCurrentStep}
          initialData={
            mode === "EDIT" || mode === "VIEW" ? stepData.courseInfo : null
          }
        />
      ),
    },
    {
      id: "step-2",
      title: "Topics & Materials",
      description: "Organize your course content and quizzes",
      component: (
        <TopicsStep
          mode={mode}
          onDataChange={handleTopicData}
          setCurrentStep={setCurrentStep}
          initialData={
            mode === "EDIT" || mode === "VIEW" ? stepData.topics : null
          }
        />
      ),
    },
    {
      id: "step-3",
      title: "Assessment Data",
      description: "Add assessment data to your course",
      component: (
        <AssessmentBuilderStep
          mode={mode}
          onDataChange={handleAssessmentData}
          setCurrentStep={setCurrentStep}
          initialData={
            mode === "EDIT" || mode === "VIEW" ? stepData.assessment : null
          }
          topicsData={stepDataRefs.current?.topics?.topics ?? []}
        />
      ),
    },
  ];

  // Render step content
  const renderStep = () => {
    return (
      <>
        {steps.map((step, index) => (
          <StepContent key={index} isActive={currentStep === index}>
            {step.component}
          </StepContent>
        ))}
      </>
    );
  };

  return (
    <div className="space-y-6">
      <Stepper
        steps={steps}
        className=""
        currentStep={currentStep}
        onStepClick={(step) => {
          if (step < currentStep) {
            handleStepNavigation("previous", step);
          }
        }}
      />

      <div className="mt-6">{renderStep()}</div>
      {mode === "VIEW" && (
        <div
          className={`flex ${currentStep === 0 ? "justify-end" : "justify-between"} mt-8`}
        >
          {currentStep > 0 && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setCurrentStep((prev) => prev - 1);
              }}
            >
              Previous
            </Button>
          )}
          {currentStep < steps.length - 1 && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setCurrentStep((prev) => prev + 1);
              }}
            >
              Next
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default CourseBuilderForm;
